import React, { useState, useEffect } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CircularProgress from '@mui/material/CircularProgress';
import Box from '@mui/material/Box';
import trainingApi from '../../services/trainingApi';

const TEST_DISPLAY_NAMES = {
  // Full keys (as stored in scenarios and DB)
  'phq-9-patient-health-questionnaire': { name: 'PHQ-9', description: 'Patient Health Questionnaire (Depression)' },
  'gad-7-generalised-anxiety-disorder': { name: 'GAD-7', description: 'Generalised Anxiety Disorder Scale' },
  'ptsd-checklist-for-dsm-5-pcl-5': { name: 'PCL-5', description: 'PTSD Checklist for DSM-5' },
  'audit-alcohol-use': { name: 'AUDIT', description: 'Alcohol Use Disorders Identification Test' },
  'pq-16-prodromal-questionnaire': { name: 'PQ-16', description: 'Prodromal Questionnaire (Psychosis Risk)' },
  'cssrs-screener': { name: 'C-SSRS', description: 'Columbia Suicide Severity Rating Scale' },
  'columbia-suicide-severity-rating-scale-screener': { name: 'C-SSRS', description: 'Columbia Suicide Severity Rating Scale' },
  'msi-bpd': { name: 'MSI-BPD', description: 'McLean Screening Instrument for BPD' },
  'msi-bpd-mclean-screening-instrument-for-borderline-personality-disorder': { name: 'MSI-BPD', description: 'McLean Screening Instrument for BPD' },
  // Short keys (fallback)
  'phq-9': { name: 'PHQ-9', description: 'Patient Health Questionnaire (Depression)' },
  'gad-7': { name: 'GAD-7', description: 'Generalised Anxiety Disorder Scale' },
  'pcl-5': { name: 'PCL-5', description: 'PTSD Checklist' },
  'audit': { name: 'AUDIT', description: 'Alcohol Use Disorders Identification Test' },
  'pq-16': { name: 'PQ-16', description: 'Prodromal Questionnaire (Psychosis Risk)' },
};

const formatTestName = (testKey) => TEST_DISPLAY_NAMES[testKey]?.name || testKey.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
const formatTestDescription = (testKey) => TEST_DISPLAY_NAMES[testKey]?.description || '';

const AssessmentRequestDialog = ({
  open,
  onClose,
  availableAssessments,
  assessmentsUsed = [],
  onRequest,
  isRequesting = false,
  visibilityLevel,
}) => {
  const [platformAssessments, setPlatformAssessments] = useState(null);
  const [loadingPlatform, setLoadingPlatform] = useState(false);

  // When availableAssessments is null (referral_only or blind mode), fetch all from platform
  const shouldLoadAll = availableAssessments === null || availableAssessments === undefined;

  useEffect(() => {
    if (!open || !shouldLoadAll || platformAssessments) return;
    let cancelled = false;
    setLoadingPlatform(true);
    trainingApi.getAvailableAssessments()
      .then(response => {
        if (cancelled) return;
        const body = response.data || response;
        const data = body.data || body;
        const assessments = data.assessments || [];
        setPlatformAssessments(assessments.map(a => a.testKey));
      })
      .catch(() => {
        if (!cancelled) setPlatformAssessments([]);
      })
      .finally(() => {
        if (!cancelled) setLoadingPlatform(false);
      });
    return () => { cancelled = true; };
  }, [open, shouldLoadAll, platformAssessments]);

  const displayAssessments = shouldLoadAll ? (platformAssessments || []) : (availableAssessments || []);
  const isLoading = shouldLoadAll && loadingPlatform;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Request Assessment</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {shouldLoadAll
            ? 'Select an assessment you believe is clinically appropriate for this patient. Choose carefully — assessment selection is a testable skill.'
            : 'Select an assessment to administer to the patient. Each assessment can only be administered once per session.'}
        </Typography>
        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
            <CircularProgress size={28} />
          </Box>
        ) : (
          <>
            <List>
              {displayAssessments.map(testKey => {
                const used = assessmentsUsed.includes(testKey);
                return (
                  <ListItemButton
                    key={testKey}
                    onClick={() => !used && !isRequesting && onRequest(testKey)}
                    disabled={used || isRequesting}
                  >
                    <ListItemText
                      primary={formatTestName(testKey)}
                      secondary={used ? 'Already administered' : formatTestDescription(testKey)}
                    />
                    {used && <CheckCircleIcon color="success" />}
                  </ListItemButton>
                );
              })}
            </List>
            {displayAssessments.length === 0 && (
              <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
                No assessments available for this scenario.
              </Typography>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
      </DialogActions>
    </Dialog>
  );
};

export default AssessmentRequestDialog;
